import React, { useState, useEffect } from 'react';
import { useHeader } from '../../context/HeaderContext';
import { useSelector } from 'react-redux';
import { changePartnerPassword } from '../../api/partner';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const ChangePassword = () => {
  const { setHeaderTitle } = useHeader();
  const { partnerInfo } = useSelector((state) => state.partner);

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setHeaderTitle('Change Password');
  }, [setHeaderTitle]);

  const validatePassword = (password) => {
    const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!validatePassword(formData.password)) {
      newErrors.password = 'Password must be at least 8 characters with 1 uppercase, 1 number, and 1 special character';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirm password is required';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      const response = await changePartnerPassword({
        id: partnerInfo.partnerId || partnerInfo._id,
        password: formData.password,
        confirmPassword: formData.confirmPassword
      });

      if (response.success) {
        toast.success('Password changed successfully!');
        // Clear form
        setFormData({
          password: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='w-full p-6'>
      <div className='max-w-2xl bg-white rounded-lg shadow-md border border-border p-6'>
        <h2 className='text-2xl font-bold text-fontBlack mb-6'>Change Password</h2>

        <div className='mb-6 p-4 bg-chipYellow bg-opacity-10 border border-chipYellow rounded-md'>
          <p className='text-sm text-fontBlack'>
            <strong>Password Requirements:</strong>
          </p>
          <ul className='text-sm text-fontBlack mt-2 list-disc list-inside'>
            <li>At least 8 characters long</li>
            <li>Contains at least one uppercase letter</li>
            <li>Contains at least one number</li>
            <li>Contains at least one special character (@$!%*?&)</li>
          </ul>
        </div>

        <form onSubmit={handleSubmit}>
          {/* New Password */}
          <div className='mb-4'>
            <label className='block text-base font-semibold text-fontBlack mb-2'>
              New Password *
            </label>
            <div className='relative'>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                className={`w-full border ${errors.password ? 'border-red' : 'border-secondary'} rounded-md px-3 py-2 pr-10 text-base outline-primary`}
                placeholder='Enter new password'
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className='absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary hover:text-primary'
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {errors.password && (
              <p className='text-red text-sm mt-1'>{errors.password}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div className='mb-6'>
            <label className='block text-base font-semibold text-fontBlack mb-2'>
              Confirm New Password *
            </label>
            <div className='relative'>
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                className={`w-full border ${errors.confirmPassword ? 'border-red' : 'border-secondary'} rounded-md px-3 py-2 pr-10 text-base outline-primary`}
                placeholder='Confirm new password'
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className='absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary hover:text-primary'
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className='text-red text-sm mt-1'>{errors.confirmPassword}</p>
            )}
          </div>

          {/* Submit Button */}
          <div className='flex justify-end gap-4'>
            <button
              type="button"
              onClick={() => {
                setFormData({ password: '', confirmPassword: '' });
                setErrors({});
              }}
              className='bg-white text-primary border-2 border-primary px-6 py-2 rounded-md font-bold text-base hover:bg-primaryBgLight'
            >
              Clear
            </button>
            <button
              type="submit"
              disabled={loading}
              className='bg-primary text-white px-6 py-2 rounded-md font-bold text-base hover:bg-primaryHoverDark disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {loading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>

      <ToastContainer
        position="bottom-right"
        autoClose={2000}
      />
    </div>
  );
};

export default ChangePassword;
